'use strict';

var AFM; // Required below due to circular dependancy issues.

/**
 * This class provides listener functionality.
 * It includes an 'Listener.init(...)' method which adds proxy methods to
 * another class -- as a result adds 'listner' features to the passed instance.
 * 
 * For example, in
 * a constructor of another class call "Listner.init(this);". That will create
 * a new instance of this class and add on(...), off(...) and trigger(...)
 * proxy methods to what was passed during 'init'.
 * 
 * @class
 */
function Listener(){}

Listener.init = function(extendedObject) {
	// TODO Rename to sugar(..) or create(..)? .. this could really modify the
	// 		prototype instead; should be called during class def not during
	// 		construction then. Look at optimizing this code.
	//		Reread https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object to get new ideas
	//		Also think about ES6 and Typescript
	
	var newListener = new Listener();
	newListener._extendedObject = extendedObject;
	extendedObject.on = newListener.on.bind(extendedObject);
	extendedObject.onEach = newListener.onEach.bind(extendedObject);
	extendedObject.once = newListener.once.bind(extendedObject);
	extendedObject.off = newListener.off.bind(extendedObject);
	extendedObject.has = newListener.has.bind(extendedObject);
	extendedObject.hasListeners = newListener.hasListeners.bind(extendedObject);
	extendedObject.trigger = newListener.trigger.bind(extendedObject);
	extendedObject.triggerDuringRead = newListener.triggerDuringRead.bind(extendedObject);
	extendedObject._triggerDuringRead = newListener._triggerDuringRead.bind(extendedObject);
	return newListener;
}

// TODO We should switch to Set or another storage format that requires unique entries.

function getListeners(self) {
	if (self._listeners === undefined) {
		self._listeners = {};
	}
	return self._listeners;
}
function getOnceListeners(self) {
	if (self._onceListeners === undefined) {
		self._onceListeners = {};
	}
	return self._onceListeners;
}


/**
 * @public
 * @param {String} type
 * @param {Funciton} listener
 * @param {boolean} [isOnce=false]
 */
Listener.prototype.on = function(type, listener, isOnce) {
	// TODO Consider using a Set when supported by the browser: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Set ... May be faster. And would I think mean running 'has' would be faster.
	
	if (! this.has(type, listener, isOnce)) { 
		var listeners = (isOnce === true)? getOnceListeners(this) : getListeners(this);
	    if (!listeners[type]) {
	    	listeners[type] = [];
	    }
	    listeners[type].push(listener);
	}
}


/**
 * @public
 * This is a helper method which iterates through the provided object
 * and adds each entry as a listener by using the 'on()' method.
 * The key is used for 'type' and the value should be the 'listener' function.
 * @param {Object} listeners
 * @param {boolean} [isOnce] - Defaults to same as the 'on()' method.
 */
Listener.prototype.onEach = function(listeners, isOnce) {
	if (listeners !== null && listeners !== undefined) {
		for (var prop in listeners) {
			if (listeners.hasOwnProperty(prop)) {
				this.on(prop, listeners[prop], isOnce);
			}
		}
	}
}

/**
 * Same as on(...) where 'isOnce' is set to true. This is a convenience method.
 * @public
 * @param {String} type
 * @param {Funciton} listener
 */
Listener.prototype.once = function(type, listener) {
	this.on(type, listener, true)
}

/**
 * @public
 * @param {String} type
 * @param {Funciton} listener
 * @param {boolean} [listener=false]
 */
Listener.prototype.off = function(type, listener, isOnce) {
	var listeners = (isOnce === true)? getOnceListeners(this) : getListeners(this);
	var listenersForEvent = listeners[type];
	if (listenersForEvent !== undefined) {
		var newList = new Array();
		for (var i=0; i < listenersForEvent.length; i++ ) {
			if (listenersForEvent[i] !== listener) {
				newList.push(listenersForEvent[i]);
			}
        }
		listeners[type] = newList;
	}
}

// TODO has() and hasListeners() is confusing and needs to be documented as to purpose
// TODO I also feel both should be changed so when 'isOnce' excluded it check both types of listeners. This comment goes for other methods too, like off().
/**
 * @public
 * @param {String} type
 * @param {Funciton} listener
 * @param {boolean} [isOnce=false]
 */
Listener.prototype.has = function(type, listener, isOnce) {
	var listeners = (isOnce === true)? getOnceListeners(this) : getListeners(this);
	var listenersForEvent = listeners[type];
	if (listenersForEvent !== undefined) {
		for (var i=0; i < listenersForEvent.length; i++ ) {
			if (listenersForEvent[i] === listener) {
				return true;
			}
        }
	}
	return false;
}

/**
 * @public
 * @param {String} type
 * @param {boolean} [isOnce=false]
 */
// TODO I also feel both should be changed so when 'isOnce' excluded it check both types of listeners. This comment goes for other methods too, like off().
Listener.prototype.hasListeners = function(type, isOnce) {
	var listeners = (isOnce === true)? getOnceListeners(this) : getListeners(this);
	var typeListeners = listeners[type];
	return (typeListeners !== undefined && typeListeners.length > 0);
}

/**
 * @param {String} type
 * @param {Object} [data=null]
 * @return {BasicCustomEvent}
 *            Returns the event object. Note it has a 'defaultPrevented' which
 *            should be checked to provide the ability for listeners to stop
 *            default behavior.
 */
Listener.prototype.trigger = function(type, data) {
	var event = new BasicCustomEvent(type);
	// TODO If there are no listeners for the types we should stop now and return event
	
	if (data === undefined) {
		data = null;
	}
	var listeners, typeListeners;
	
	// Call all once listeners
	listeners = getOnceListeners(this);
	typeListeners = listeners[type];
	if (typeListeners !== undefined) {
		var once;
		while((once = typeListeners.pop()) !== undefined) {
			once.call(this, event, data);
			if (event._stopPropagation) {
	    		break;
	    	}
		}
	}
	
	if (event._stopPropagation) {
		return event;
	}

	// Call all standard listeners
	listeners = getListeners(this);
	typeListeners = listeners[type];
	if (typeListeners !== undefined) {
		for (var i=0; i < typeListeners.length; i++) {
			typeListeners[i].call(this, event, data);
	    	if (event._stopPropagation) {
	    		break;
	    	}
		}
	}
	
	return event;
}

/**
 * Does the same thing as trigger() but dispatches the event
 * during the "read" phase of FrameManager. If multiple events
 * of the same type are reported during one frame, only the
 * most recent will be reported. This method also does
 * not have a return value because it is asynchronous.
 */
Listener.prototype.triggerDuringRead = function(type, data) {
	var isNoWait = this._listenersTriggersForRead === null
		|| this._listenersTriggersForRead === undefined;
	
	if (isNoWait) {
		this._listenersTriggersForRead = {};
	}
	this._listenersTriggersForRead[type] = data;
	
	if (isNoWait) {
		if (AFM === undefined) {
			AFM = require('../animate/FrameManager.js')
		}
		AFM.once('read', this._triggerDuringRead);
	}
}
Listener.prototype._triggerDuringRead = function() {
	var listeners = this._listenersTriggersForRead;
	for (var type in listeners) {
		if (listeners.hasOwnProperty(type)) {
			this.trigger(type, listeners[type]);
		}
	}
	this._listenersTriggersForRead = null;
}


// TODO Consider making this a separate file so it an be extended. And make it so when a class is setup with Listners that it can provide a reference to its own constructor that extends BasicCustomEvent to be used.
function BasicCustomEvent(type) {
	this.type = type;
	this._stopPropagation = false;
	this.defaultPrevented = false;
}
BasicCustomEvent.prototype.stopPropagation = function() {
	this._stopPropagation = true;
}
BasicCustomEvent.prototype.preventDefault = function() {
	this.defaultPrevented = true;
}

	
module.exports = Listener;
